import { request } from './client';
import type {
  BoardTheme,
  ThemeWithOwnership,
  ThemeCategory,
  CreateThemeInput,
  UpdateThemeInput,
  ThemeUsageStats,
} from '../types';

// Response types
export interface ThemeListResponse {
  themes: ThemeWithOwnership[];
}

export interface ThemeCategoriesResponse {
  categories: ThemeCategory[];
}

export interface ApplyThemeResponse {
  message: string;
  themeId: string;
}

export interface RemoveThemeResponse {
  message: string;
}

export interface PurchaseThemeResponse {
  theme: BoardTheme;
  creditsSpent: number;
  remainingCredits: number;
}

// Public theme endpoints (optional auth for ownership status)
export const themesApi = {
  // List all available themes (with ownership status if authenticated)
  async listThemes(): Promise<ThemeListResponse> {
    return request<ThemeListResponse>('/api/squareup/themes');
  },

  // Get a specific theme by ID or lookup key
  async getTheme(idOrKey: string): Promise<ThemeWithOwnership> {
    return request<ThemeWithOwnership>(`/api/squareup/themes/${idOrKey}`);
  },

  // Get available theme categories
  async getCategories(): Promise<ThemeCategoriesResponse> {
    return request<ThemeCategoriesResponse>('/api/squareup/themes/categories');
  },

  // List themes by category
  async listByCategory(category: ThemeCategory): Promise<ThemeListResponse> {
    return request<ThemeListResponse>(`/api/squareup/themes/category/${category}`);
  },

  // Get user's owned themes (requires auth)
  async getOwnedThemes(): Promise<ThemeListResponse> {
    return request<ThemeListResponse>('/api/squareup/themes/owned');
  },

  // Apply a theme to a board (requires auth + board management permission)
  async applyThemeToBoard(boardId: string, themeId: string): Promise<ApplyThemeResponse> {
    return request<ApplyThemeResponse>(`/api/squareup/boards/${boardId}/theme`, {
      method: 'PUT',
      body: { themeId },
    });
  },

  // Remove theme from a board (requires auth + board management permission)
  async removeThemeFromBoard(boardId: string): Promise<RemoveThemeResponse> {
    return request<RemoveThemeResponse>(`/api/squareup/boards/${boardId}/theme`, {
      method: 'DELETE',
    });
  },

  // Purchase a theme using credits (requires auth)
  async purchaseTheme(themeId: string): Promise<PurchaseThemeResponse> {
    return request<PurchaseThemeResponse>('/api/squareup/themes/purchase', {
      method: 'POST',
      body: { themeId },
    });
  },
};

// Theme purchase record type
export interface ThemePurchaseRecord {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  themeId: string;
  themeName: string;
  themeCategory: string;
  creditPrice: number;
  acquiredAt: string;
  transactionId?: string;
}

export interface ThemePurchaseListResult {
  purchases: ThemePurchaseRecord[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCredits: number;
}

export interface ThemePurchaseStats {
  totalPurchases: number;
  byCategory: { category: string; count: number }[];
  last7Days: number;
  topThemes: { themeId: string; themeName: string; count: number }[];
}

// Admin theme management endpoints
export const adminThemesApi = {
  // List all themes (including inactive)
  async listAllThemes(): Promise<{ themes: BoardTheme[] }> {
    return request<{ themes: BoardTheme[] }>('/api/squareup/admin/themes');
  },

  // Create a new theme
  async createTheme(input: CreateThemeInput): Promise<BoardTheme> {
    return request<BoardTheme>('/api/squareup/admin/themes', {
      method: 'POST',
      body: input,
    });
  },

  // Update a theme
  async updateTheme(themeId: string, input: UpdateThemeInput): Promise<BoardTheme> {
    return request<BoardTheme>(`/api/squareup/admin/themes/${themeId}`, {
      method: 'PUT',
      body: input,
    });
  },

  // Delete a theme
  async deleteTheme(themeId: string): Promise<{ message: string }> {
    return request<{ message: string }>(`/api/squareup/admin/themes/${themeId}`, {
      method: 'DELETE',
    });
  },

  // Get theme usage statistics
  async getThemeUsageStats(): Promise<{ stats: ThemeUsageStats[] }> {
    return request<{ stats: ThemeUsageStats[] }>('/api/squareup/admin/themes/stats');
  },

  // List theme purchases (admin only)
  async listPurchases(params?: {
    page?: number;
    pageSize?: number;
    themeId?: string;
    userId?: string;
  }): Promise<ThemePurchaseListResult> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
    if (params?.themeId) searchParams.set('themeId', params.themeId);
    if (params?.userId) searchParams.set('userId', params.userId);

    const query = searchParams.toString();
    const url = `/api/squareup/admin/themes/purchases${query ? `?${query}` : ''}`;
    return request<ThemePurchaseListResult>(url);
  },

  // Get theme purchase stats (admin only)
  async getPurchaseStats(): Promise<ThemePurchaseStats> {
    return request<ThemePurchaseStats>('/api/squareup/admin/themes/purchases/stats');
  },
};
